import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { JOB_PHASES, PHASE_MAX_INDEX, isPhaseComplete } from '@/lib/jobPhases';
import { advancePhase } from '@/lib/jobsData';
import { translatePhase } from '@/lib/i18nLabels';
import { toast } from 'sonner';

const PhaseStep = ({ phase, current, isLast, t }) => {
  const idx = phase.index;
  const status = idx < current ? 'done' : idx === current ? 'current' : 'pending';

  return (
    <div
      className="flex items-start gap-3"
      data-testid={`phase-step-${phase.key}`}
      data-status={status}
    >
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'size-7 rounded-full border flex items-center justify-center text-[11px] font-semibold transition-colors',
            status === 'done' &&
              'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border-[hsl(var(--success))]/40',
            status === 'current' &&
              'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/30',
            status === 'pending' &&
              'bg-[hsl(var(--muted))] text-muted-foreground border-border'
          )}
        >
          {status === 'done' ? <Check size={14} strokeWidth={2.5} /> : idx + 1}
        </div>
        {!isLast ? (
          <span
            aria-hidden
            className={cn(
              'mt-1 w-px flex-1 min-h-6',
              status === 'done' ? 'bg-[hsl(var(--success))]/40' : 'bg-border'
            )}
          />
        ) : null}
      </div>
      <div className="pb-4 pt-0.5">
        <div
          className={cn(
            'text-sm font-medium',
            status === 'current'
              ? 'text-[hsl(var(--primary))]'
              : status === 'done'
              ? 'text-foreground'
              : 'text-muted-foreground'
          )}
        >
          {translatePhase(t, phase.key)}
        </div>
      </div>
    </div>
  );
};

export const JobPhasesTab = ({ job, onUpdated }) => {
  const { t } = useTranslation();
  const phase = Number(job?.phase) || 0;
  const complete = isPhaseComplete(phase);

  const [advancing, setAdvancing] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      const updated = await advancePhase(job);
      const nextKey =
        JOB_PHASES[Math.min(PHASE_MAX_INDEX, (Number(job.phase) || 0) + 1)].key;
      toast.success(
        t('jobs.phases.advance_toast', { phase: translatePhase(t, nextKey) })
      );
      onUpdated?.(updated);
      setConfirmAdvance(false);
    } catch (e) {
      toast.error(e?.message || t('jobs.phases.advance_failed'));
    } finally {
      setAdvancing(false);
    }
  };

  const nextPhaseLabel = complete
    ? null
    : translatePhase(t, JOB_PHASES[phase + 1].key);

  return (
    <div className="space-y-5" data-testid="job-phases-tab">
      {/* Phase tracker */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t('jobs.phases.phase_tracker')}
              </div>
              <div className="text-base font-semibold text-foreground">
                {complete
                  ? t('jobs.phases.complete')
                  : translatePhase(t, JOB_PHASES[phase].key)}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setConfirmAdvance(true)}
              disabled={complete || advancing}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="phases-advance-button"
            >
              {advancing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('jobs.phases.advancing')}
                </>
              ) : complete ? (
                <>{t('jobs.phases.complete')}</>
              ) : (
                <>
                  {t('jobs.phases.advance')}
                  <ChevronRight size={14} className="ml-1" />
                </>
              )}
            </Button>
          </div>
          <div className="pl-1">
            {JOB_PHASES.map((p, i) => (
              <PhaseStep
                key={p.key}
                phase={p}
                current={phase}
                isLast={i === JOB_PHASES.length - 1}
                t={t}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmAdvance} onOpenChange={setConfirmAdvance}>
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="advance-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('jobs.phases.advance_confirm_title', { phase: nextPhaseLabel || '' })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('jobs.phases.advance_confirm_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              disabled={advancing}
            >
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdvance}
              disabled={advancing}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="advance-confirm-action"
            >
              {advancing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('jobs.phases.advancing')}
                </>
              ) : (
                <>{t('jobs.phases.advance_phase')}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobPhasesTab;
